import * as React from 'react';
import { IItemCache } from '../../../models/IItemCacheObserver';
import {
  isNewFacet,
  isCheckedOutFacet,
  malwareDetectedFacet,
  missingMetadataFacet,
  policyTipFacet,
  displayNameFacet,
  showRetentionPolicyIconFacet,
  followedItemFacet,
  isFollowedFacet,
  analyticsItemFacet,
  isSharedFacet
} from '../../../facets/ItemFacets';
import { isTrendingFacet } from '../../../facets/ItemAnalyticsFacets';
import {
  enableTrendingConfigurationFacet,
  enableReadOnlyConfigurationFacet
} from '../../../facets/ConfigurationFacets';
import {
  NewSignal,
  MalwareDetectedSignal,
  YouCheckedOutSignal,
  SomeoneCheckedOutSignal,
  MissingMetadataSignal,
  BlockedSignal,
  WarningSignal,
  RecordSignal,
  FollowedSignal,
  TrendingSignal,
  ReadOnlySignal,
  SharedSignal
} from '@uifabric/experiments/lib/components/signals/Signals';
import { PolicyTipType } from '../../../models/ItemPolicy';
import * as StringHelper from '@ms/utilities-strings/lib/index';
import strings from '../FieldStrings.resx';
import {
  ISignalData,
  itemLifeCycleSignalsControl,
  IItemSignalControlProps,
  IItemSignalControlOverrides,
  itemSocialSignalsControl,
  itemStorageSignalsControl
} from '../../../controls/itemLifeCycleSignalsControl';
import { Facet } from '../../../models/Facet';
import { isReadOnlyFacet } from '../../../facets/computed/isReadOnlyFacet';
import { ControlFacet } from '../../../models/controls/ControlFacet';
import { getConfiguration } from '../../../models/configuration/getConfiguration';

interface ISignalHandler {
  (itemCache: IItemCache, itemKey: string): ISignalData | null;
}

const lifeCycleSignalHandlers: ISignalHandler[] = [
  getNewSignal,
  getMalwareSignal,
  getYouCheckedOutSignal,
  getDlpBlockedSignal,
  getMissingMetadataSignal,
  getDlpWarningSignal,
  getAwaitingApprovalSignal,
  getTrendingSignal,
  getSomeoneCheckedOutSignal,
  getRetentionRecordSignal,
  getFollowedSignal,
  // 'Read-only' is a social signal, but putting as a life-cycle signal
  // until social signal support is ready.
  getReadOnlySignal
];

const socialSignalHandlers: ISignalHandler[] = [
  getSharedSignal
];

const storageSignalHandlers: ISignalHandler[] = [];

export const newSignalKey = new Facet('newSignal').id;
export const youCheckedOutSignalKey = new Facet('youCheckedOutSignal').id;
export const someoneCheckedOutSignalKey = new Facet('someoneCheckedOutSignal').id;
export const malwareSignalKey = new Facet('malwareSignal').id;
export const dlpBlockedSignalKey = new Facet('dlpBlockedSignal').id;
export const missingMetadataSignalKey = new Facet('missingMetadataSignal').id;
export const dlpWarningSignalKey = new Facet('dlpWarningSignal').id;
export const retentionRecordSignalKey = new Facet('retentionRecordSignal').id;
export const followedSignalKey = new Facet('followedSignal').id;
export const trendingSignalKey = new Facet('trendingSignal').id;
export const readOnlySignalKey = new Facet('readOnlySignal').id;
export const sharedSignalKey = new Facet('sharedSignal').id;

export { ISignalData };

export function getStorageSignals(itemCache: IItemCache, itemKey: string): ISignalData[] {
  return getSignals(itemCache, {
    itemKey,
    control: itemStorageSignalsControl,
    signalHandlers: storageSignalHandlers,
    maxCount: 1
  });
}

export function getSocialSignals(itemCache: IItemCache, itemKey: string): ISignalData[] {
  return getSignals(itemCache, {
    itemKey,
    control: itemSocialSignalsControl,
    signalHandlers: socialSignalHandlers,
    maxCount: 1
  });
}

export function getLifeCycleSignals(
  itemCache: IItemCache,
  itemKey: string,
  maxCount: number,
  includeNew: boolean
): ISignalData[] {
  return getSignals(itemCache, {
    itemKey,
    control: itemLifeCycleSignalsControl,
    signalHandlers: lifeCycleSignalHandlers,
    maxCount,
    offset: includeNew ? 0 : 1
  });
}

function getSignals(itemCache: IItemCache, props: {
  itemKey: string;
  control: ControlFacet<IItemSignalControlProps, IItemSignalControlOverrides>;
  signalHandlers: ISignalHandler[];
  maxCount: number;
  offset?: number;
}): ISignalData[] {
  const {
    itemKey,
    control,
    signalHandlers,
    maxCount,
    offset
  } = props;

  const signals: ISignalData[] = [];
  let funcIndex = offset || 0;

  while (signals.length < maxCount && funcIndex < signalHandlers.length) {
    const getSignalFunc = signalHandlers[funcIndex];
    const signal = getSignalFunc(itemCache, itemKey);
    if (signal) {
      signals.push(signal);
    }
    funcIndex++;
  }

  const controlHandler = itemCache.demandItemFacet(control, itemKey);
  const {
    signals: finalSignals = []
  } = control.evaluate(controlHandler)(itemCache, {
    itemKey,
    signals
  });

  return finalSignals;
}

const newSignal = {
  signal: (
    <NewSignal
      key={ newSignalKey }
      ariaLabel={ strings.itemSignalNew }
      title={ strings.itemSignalNewTooltip }
    />
  ),
  title: strings.itemSignalNewTooltip,
  key: newSignalKey
};

export function getNewSignal(itemCache: IItemCache, itemKey: string): ISignalData | null {
  const isNew = itemCache.demandItemFacet(isNewFacet, itemKey);
  return isNew ? newSignal : null;
}

const youCheckedOutSignal = {
  signal: (
    <YouCheckedOutSignal
      key={ youCheckedOutSignalKey }
      ariaLabel={ strings.itemSignalYouCheckedOut }
      title={ strings.itemSignalYouCheckedOutTooltip }
    />
  ),
  title: strings.itemSignalYouCheckedOutTooltip,
  key: youCheckedOutSignalKey
};

export function getYouCheckedOutSignal(itemCache: IItemCache, itemKey: string): ISignalData | null {
  const isCheckedOut = itemCache.demandItemFacet(isCheckedOutFacet, itemKey);
  if (isCheckedOut && isCheckedOut.byYou) {
    return youCheckedOutSignal;
  }
  return null;
}

export function getSomeoneCheckedOutSignal(itemCache: IItemCache, itemKey: string): ISignalData | null {
  const isCheckedOut = itemCache.demandItemFacet(isCheckedOutFacet, itemKey);
  const checkedOutBy = isCheckedOut && isCheckedOut.byOther;
  if (checkedOutBy) {
    const displayName = itemCache.demandItemFacet(displayNameFacet, checkedOutBy);

    const titleString = displayName ?
      StringHelper.format(strings.itemSignalSomeoneCheckedOutTooltip, displayName) :
      strings.itemSignalSomeoneCheckedOut;

    return {
      signal: (
        <SomeoneCheckedOutSignal
          key={ someoneCheckedOutSignalKey }
          ariaLabel={ strings.itemSignalSomeoneCheckedOut }
          title={ titleString }
        />
      ),
      title: titleString,
      key: someoneCheckedOutSignalKey
    };
  }
  return null;
}

const malwareSignal = {
  signal: (
    <MalwareDetectedSignal
      key={ malwareSignalKey }
      ariaLabel={ strings.itemSignalMalwareDetected }
      title={ strings.itemSignalMalwareDetectedTooltip }
    />
  ),
  title: strings.itemSignalMalwareDetectedTooltip,
  key: malwareSignalKey
};

export function getMalwareSignal(itemCache: IItemCache, itemKey: string): ISignalData | null {
  const isMalwareDetected = itemCache.demandItemFacet(malwareDetectedFacet, itemKey);
  return isMalwareDetected ? malwareSignal : null;
}

const dlpBlockedSignal = {
  signal: (
    <BlockedSignal
      key={ dlpBlockedSignalKey }
      ariaLabel={ strings.itemSignalBlocked }
      title={ strings.itemSignalBlockedTooltip }
    />
  ),
  title: strings.itemSignalBlockedTooltip,
  key: dlpBlockedSignalKey
};

export function getDlpBlockedSignal(itemCache: IItemCache, itemKey: string): ISignalData | null {
  const policyTip = itemCache.demandItemFacet(policyTipFacet, itemKey);
  if (policyTip &&
    (policyTip === PolicyTipType.block ||
      policyTip === PolicyTipType.notifyAndBlock)) {
    return dlpBlockedSignal;
  }
  return null;
}

const missingMetadataSignal = {
  signal: (
    <MissingMetadataSignal
      key={ missingMetadataSignalKey }
      ariaLabel={ strings.itemSignalMissingMetadata }
      title={ strings.itemSignalMissingMetadataTooltip }
    />
  ),
  title: strings.itemSignalMissingMetadataTooltip,
  key: missingMetadataSignalKey
};

export function getMissingMetadataSignal(itemCache: IItemCache, itemKey: string): ISignalData | null {
  const hasMissingMetadata = itemCache.demandItemFacet(missingMetadataFacet, itemKey);
  return hasMissingMetadata ? missingMetadataSignal : null;
}

const dlpWarningSignal = {
  signal: (
    <WarningSignal
      key={ dlpWarningSignalKey }
      ariaLabel={ strings.itemSignalWarning }
      title={ strings.itemSignalWarningTooltip }
    />
  ),
  title: strings.itemSignalWarningTooltip,
  key: dlpWarningSignalKey
};

export function getDlpWarningSignal(itemCache: IItemCache, itemKey: string): ISignalData | null {
  const policyTip = itemCache.demandItemFacet(policyTipFacet, itemKey);
  if (policyTip && policyTip === PolicyTipType.notify) {
    return dlpWarningSignal;
  }
  return null;
}

const retentionRecordSignal = {
  signal: (
    <RecordSignal
      key={ retentionRecordSignalKey }
      ariaLabel={ strings.itemSignalIsRecord }
      title={ strings.itemSignalIsRecordTooltip }
    />
  ),
  title: strings.itemSignalIsRecordTooltip,
  key: retentionRecordSignalKey
};

export function getRetentionRecordSignal(itemCache: IItemCache, itemKey: string): ISignalData | null {
  const showRecordIcon = itemCache.demandItemFacet(showRetentionPolicyIconFacet, itemKey);
  return showRecordIcon ? retentionRecordSignal : null;
}

export function getAwaitingApprovalSignal(itemCache: IItemCache, itemKey: string): ISignalData | null {
  // to be implemented
  return null;
}

const trendingSignal = {
  signal: (
    <TrendingSignal
      key={ trendingSignalKey }
      ariaLabel={ strings.itemSignalTrending }
      title={ strings.itemSignalIsTrendingTooltip }
    />
  ),
  title: strings.itemSignalIsTrendingTooltip,
  key: trendingSignalKey
};

export function getTrendingSignal(itemCache: IItemCache, itemKey: string): ISignalData | null {
  const {
    isTrendingEnabled
  } = getConfiguration(itemCache, {
    isTrendingEnabled: enableTrendingConfigurationFacet
  });

  if (!isTrendingEnabled) {
    return null;
  }
  const analyticsKey = itemCache.demandItemFacet(analyticsItemFacet, itemKey);
  const itemAnalyticsKey = analyticsKey && analyticsKey.itemKey;
  const isTrending = itemAnalyticsKey && itemCache.demandItemFacet(isTrendingFacet, itemAnalyticsKey);

  return isTrending ? trendingSignal : null;
}

const followedSignal = {
  signal: (
    <FollowedSignal
      key={ followedSignalKey }
      ariaLabel={ strings.itemSignalIsFollowed }
      title={ strings.itemSignalIsFollowed }
    />
  ),
  title: strings.itemSignalIsFollowed,
  key: followedSignalKey
};

export function getFollowedSignal(itemCache: IItemCache, itemKey: string): ISignalData | null {
  const followedItem = itemCache.demandItemFacet(followedItemFacet, itemKey);

  const isFollowed = itemCache.demandItemFacet(isFollowedFacet, followedItem && followedItem.itemKey);

  return isFollowed ? followedSignal : null;
}

const sharedSignal = {
  signal: (
    <SharedSignal
      key={ sharedSignalKey }
      ariaLabel={ strings.sharedColumnSharedFieldText }
    />
  ),
  key: sharedSignalKey
};

export function getSharedSignal(itemCache: IItemCache, itemKey: string): ISignalData | null {
  const isShared = itemCache.demandItemFacet(isSharedFacet, itemKey);

  return isShared ? sharedSignal : null;
}

const readOnlySignal = {
  signal: (
    <ReadOnlySignal
      key={ readOnlySignalKey }
      ariaLabel={ strings.itemSignalReadOnly }
      title={ strings.itemSignalReadOnly }
    />
  ),
  key: readOnlySignalKey
};

export function getReadOnlySignal(itemCache: IItemCache, itemKey: string): ISignalData | null {
  const {
    isReadOnlyEnabled
  } = getConfiguration(itemCache, {
    isReadOnlyEnabled: enableReadOnlyConfigurationFacet
  });

  if (!isReadOnlyEnabled) {
    return null;
  }

  const isReadOnly = itemCache.demandItemFacet(isReadOnlyFacet, itemKey);

  return isReadOnly ? readOnlySignal : null;
}
